import { CSSProperties } from "react";
import { useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { selectCurrentLanguage } from "../../../app/slices/TrainingLocalizationSlice";
import { selectSequenceVariables } from "../../../app/slices/TrainingSlice";
import {
  addPowerSlideItem,
  canAddPowerSlideItem,
  canRemovePowerSlideItem,
  getPowerSlideAssetId,
  getPowerSlideFieldKeys,
  ISlide,
  removePowerSlideItem,
  setPowerSlideAsset,
} from "../../../models/interactions/PowerSlideHelper";
import {
  getPowerSlideTemplate,
  POWER_SLIDE_TITLE_FIELD,
  PowerSlideTemplate,
  PowerSlideTextField,
} from "../../../models/interactions/PowerSlideTemplates";
import {
  powerSlideBlockPadding,
  powerSlideMedia,
  powerSlideMetrics,
  powerSlideOverlay,
  powerSlidePadding,
  powerSlideRadius,
  powerSlideType,
  PowerSlideTypeStyle,
} from "./PowerSlideDesign";
import PowerSlideAssetSlot, {
  PowerSlideVideoChrome,
} from "./PowerSlideAssetSlot";
import PowerSlideStage from "./PowerSlideStage";
import PowerSlideTextBlock from "./PowerSlideTextBlock";
import { getPowerSlideSurface } from "./PowerSlideSurfaces";
import {
  DEFAULT_POWER_SLIDE_THEME,
  PowerSlideTheme,
} from "../../../models/interactions/PowerSlideTheme";

interface PowerSlideCanvasProps {
  slide: ISlide;
  getValue: (key: string) => string;
  changeValue: (key: string, value: string, shortcode?: string) => void;
  onUpdateSlide: (slide: ISlide) => void;
  getNewKey: () => string;
  theme?: PowerSlideTheme;
  /**
   * Render the slide as it plays: no affordances, no controls, empty regions
   * hidden. Used for the rail's miniatures, so a thumbnail is the real slide
   * rather than a second implementation that can drift.
   */
  readOnly?: boolean;
}

const column: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
};

function field(
  template: PowerSlideTemplate,
  fieldId: string,
): PowerSlideTextField | undefined {
  return template.textFields.find((candidate) => candidate.id === fieldId);
}

export default function PowerSlideCanvas(props: PowerSlideCanvasProps) {
  const {
    slide,
    getValue,
    changeValue,
    onUpdateSlide,
    getNewKey,
    theme = DEFAULT_POWER_SLIDE_THEME,
    readOnly = false,
  } = props;
  // Read once for the whole slide rather than once per text region: a rail full
  // of thumbnails is a lot of blocks, and both selectors are deck-wide anyway.
  const variables = useSelector(selectSequenceVariables);
  const languageShortcode = useSelector(selectCurrentLanguage).shortcode;
  const template = getPowerSlideTemplate(slide.templateId);

  if (!template) {
    return (
      <div className="flex aspect-video w-full items-center justify-center rounded-[6px] border border-solid border-creator-error px-[24px] text-center text-creator-error">
        Unknown slide template "{slide.templateId}"
      </div>
    );
  }

  const surface = getPowerSlideSurface(template.surface, theme);
  const centred = template.align === "center";
  const slidePadding = centred
    ? powerSlidePadding.centre
    : powerSlidePadding.left;
  const blockPadding = centred
    ? powerSlideBlockPadding.centred
    : powerSlideBlockPadding.standard;

  const keyFor = (fieldId: string, index = 0) =>
    getPowerSlideFieldKeys(slide, fieldId)[index] ?? "";

  const textBlock = (
    fieldId: string,
    typeStyle: PowerSlideTypeStyle,
    color: string,
    overrides?: {
      padding?: { paddingInline: number; paddingBlock: number };
      hug?: boolean;
      index?: number;
      localizationKey?: string;
      label?: string;
    },
  ) => {
    const definition = field(template, fieldId);
    if (!definition) return null;
    const index = overrides?.index;
    const padding = overrides?.padding ?? blockPadding;
    const label = overrides?.label ?? definition.label;

    return (
      <PowerSlideTextBlock
        localizationKey={overrides?.localizationKey ?? keyFor(fieldId, index)}
        placeholder={index === undefined ? label : `${label} ${index + 1}`}
        ariaLabel={`powerslide-field-${fieldId}${index === undefined ? "" : `-${index + 1}`}`}
        typeStyle={typeStyle}
        color={color}
        surface={surface}
        paddingInline={padding.paddingInline}
        paddingBlock={padding.paddingBlock}
        align={centred ? "center" : "left"}
        hug={overrides?.hug}
        readOnly={readOnly}
        variables={variables}
        languageShortcode={languageShortcode}
        getValue={getValue}
        changeValue={changeValue}
      />
    );
  };

  /** Title slide: centred title over an optional subtitle on the feature ground. */
  const titleLayout = (
    <div
      style={{
        ...column,
        alignItems: "center",
        gap: powerSlideMetrics.titleContentGap,
        width: "100%",
      }}
    >
      {textBlock(POWER_SLIDE_TITLE_FIELD, "title", surface.ink)}
      {textBlock("subtitle", "subtitle", surface.ink)}
    </div>
  );

  /** Section slide: rule-flanked eyebrow, oversized title, subtitle. */
  const sectionLayout = (
    <div
      style={{
        ...column,
        alignItems: "center",
        gap: powerSlideMetrics.sectionContentGap,
        width: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: powerSlideMetrics.eyebrowRowGap,
        }}
      >
        <div
          style={{
            width: powerSlideMetrics.eyebrowRuleWidth,
            height: 1,
            backgroundColor: surface.divider,
          }}
        />
        {textBlock("eyebrow", "eyebrow", surface.ink, {
          padding: powerSlideBlockPadding.standard,
          hug: true,
        })}
        <div
          style={{
            width: powerSlideMetrics.eyebrowRuleWidth,
            height: 1,
            backgroundColor: surface.divider,
          }}
        />
      </div>
      {textBlock(POWER_SLIDE_TITLE_FIELD, "sectionTitle", surface.ink, {
        padding: powerSlideBlockPadding.standard,
      })}
      {textBlock("subtitle", "subtitle", surface.ink, {
        padding: powerSlideBlockPadding.standard,
      })}
    </div>
  );

  /** Basic slide: title, full-width rule, body. */
  const basicLayout = (
    <div
      style={{ ...column, gap: powerSlideMetrics.contentGap, width: "100%" }}
    >
      <div
        style={{
          ...column,
          gap: powerSlideMetrics.basicHeaderGap,
          width: "100%",
        }}
      >
        {textBlock(POWER_SLIDE_TITLE_FIELD, "slideTitle", surface.ink)}
        <div
          style={{
            width: "100%",
            paddingInline: blockPadding.paddingInline,
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              height: powerSlideMetrics.dividerHeight,
              backgroundColor: surface.divider,
            }}
          />
        </div>
      </div>
      {textBlock("body", "body", surface.ink)}
    </div>
  );

  const listMarker = (index: number) =>
    template.listMarker === "number" ? (
      <div
        style={{
          width: powerSlideMetrics.badgeSize,
          height: powerSlideMetrics.badgeSize,
          borderRadius: powerSlideRadius.pill,
          backgroundColor: surface.badgeFill,
          color: surface.accent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          ...powerSlideType.listNumber,
        }}
        aria-hidden="true"
      >
        {index + 1}
      </div>
    ) : (
      // A plain filled circle rather than the exported SVG, so the theme
      // recolours it the way every other accent is recoloured.
      <div
        style={{
          width: powerSlideMetrics.dotSize,
          height: powerSlideMetrics.dotSize,
          borderRadius: powerSlideRadius.pill,
          backgroundColor: surface.accent,
          flexShrink: 0,
        }}
        aria-hidden="true"
      />
    );

  /** Key Points and Numbered: one layout, different marker. */
  const listLayout = () => {
    const listField = template.textFields.find((entry) => entry.repeating);
    if (!listField) return null;
    const keys = getPowerSlideFieldKeys(slide, listField.id);

    return (
      <div
        style={{ ...column, gap: powerSlideMetrics.contentGap, width: "100%" }}
      >
        <div
          style={{
            ...column,
            gap: powerSlideMetrics.listHeaderGap,
            width: "100%",
          }}
        >
          {textBlock(POWER_SLIDE_TITLE_FIELD, "slideTitle", surface.ink)}
          <div style={{ paddingLeft: blockPadding.paddingInline }}>
            <div
              style={{
                width: powerSlideMetrics.accentWidth,
                height: powerSlideMetrics.accentHeight,
                borderRadius: powerSlideMetrics.accentHeight / 2,
                backgroundColor: surface.accent,
              }}
            />
          </div>
        </div>

        <div
          style={{ ...column, gap: powerSlideMetrics.listGap, width: "100%" }}
        >
          {keys.map((localizationKey, index) => (
            <div
              key={localizationKey || index}
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: powerSlideMetrics.listRowGap,
                paddingLeft: blockPadding.paddingInline,
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {listMarker(index)}
              <div style={{ flex: "1 0 0", minWidth: 0 }}>
                {textBlock(listField.id, "body", surface.ink, {
                  padding: powerSlideBlockPadding.listItem,
                  index,
                  localizationKey,
                })}
              </div>
              {/* Parked in the slide's own padding rather than in the row, so
                  the text measure is identical to what the runtime renders. */}
              {!readOnly && (
                <button
                  type="button"
                  aria-label={`powerslide-remove-${listField.id}-${index + 1}`}
                  disabled={!canRemovePowerSlideItem(slide, listField.id)}
                  onClick={() =>
                    onUpdateSlide(
                      removePowerSlideItem(slide, listField.id, index),
                    )
                  }
                  style={{
                    position: "absolute",
                    right: -44,
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 32,
                    borderRadius: powerSlideRadius.pill,
                    color: surface.ink,
                  }}
                  // Editor chrome, so it fades rather than taking a lighter ink:
                  // the surface has exactly one text colour and this is not text.
                  className="opacity-70 disabled:opacity-30"
                >
                  <CloseIcon style={{ width: 22, height: 22 }} />
                </button>
              )}
            </div>
          ))}

          {!readOnly && canAddPowerSlideItem(slide, listField.id) && (
            <button
              type="button"
              aria-label={`powerslide-add-${listField.id}`}
              onClick={() =>
                onUpdateSlide(
                  addPowerSlideItem(slide, listField.id, (amount) =>
                    Array.from({ length: amount }, getNewKey),
                  ),
                )
              }
              style={{
                // Geometry stays inline: this button lives inside the scaled
                // 1280px stage, so its sizing belongs with the slide's design
                // values rather than with editor-chrome utilities.
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginLeft: blockPadding.paddingInline,
                paddingInline: powerSlideBlockPadding.listItem.paddingInline,
                paddingBlock: powerSlideBlockPadding.listItem.paddingBlock,
                border: `1px solid ${surface.fieldBorder}`,
                borderRadius: powerSlideRadius.sm,
                color: surface.accent,
                ...powerSlideType.caption,
              }}
            >
              <AddIcon style={{ width: 22, height: 22 }} />
              Add {listField.label.toLowerCase()}
            </button>
          )}
        </div>
      </div>
    );
  };

  /** By the Numbers: three stat columns of value over label. */
  const statsLayout = () => {
    const values = field(template, "values");
    const labels = field(template, "labels");
    if (!values || !labels) return null;
    const valueKeys = getPowerSlideFieldKeys(slide, values.id);
    const labelKeys = getPowerSlideFieldKeys(slide, labels.id);

    return (
      <>
        {textBlock(POWER_SLIDE_TITLE_FIELD, "slideTitle", surface.ink, {
          padding: powerSlideBlockPadding.compact,
        })}
        {/* The columns and the add control are centred as one group. Left as
            siblings of the flex-filled row, the button would be pushed to the
            bottom of the slide instead of sitting just under the stats. */}
        <div
          style={{
            display: "flex",
            flex: "1 0 0",
            minHeight: 0,
            flexDirection: "column",
            justifyContent: "center",
            gap: 28,
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: powerSlideMetrics.statsRowGap,
              width: "100%",
            }}
          >
            {valueKeys.map((valueKey, index) => (
              <div
                key={valueKey || index}
                style={{
                  position: "relative",
                  display: "flex",
                  flex: "1 0 0",
                  minWidth: 0,
                  flexDirection: "column",
                  alignItems: "center",
                  gap: powerSlideMetrics.statColumnGap,
                }}
              >
                {!readOnly && (
                  <button
                    type="button"
                    aria-label={`powerslide-remove-values-${index + 1}`}
                    disabled={!canRemovePowerSlideItem(slide, values.id)}
                    onClick={() =>
                      onUpdateSlide(
                        removePowerSlideItem(slide, values.id, index),
                      )
                    }
                    style={{
                      position: "absolute",
                      right: 0,
                      top: -34,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 32,
                      height: 32,
                      borderRadius: powerSlideRadius.pill,
                      color: surface.ink,
                    }}
                    className="opacity-70 disabled:opacity-30"
                  >
                    <CloseIcon style={{ width: 22, height: 22 }} />
                  </button>
                )}
                <PowerSlideTextBlock
                  localizationKey={valueKey}
                  placeholder={`${values.label} ${index + 1}`}
                  // A worded prompt at 80px needs about 280px and the
                  // four-column track is 250px, so the hint - not the wording -
                  // steps down. One stop down the ramp rather than all the way
                  // to body: "Value 1" needs about 154px at 44px, which clears
                  // the narrowest track, and the step still reads as the same
                  // figure rather than as a different element.
                  //
                  // The line box still comes from the 80px strut, so an empty
                  // column is exactly as tall as a filled one and nothing moves
                  // when the first character is typed.
                  placeholderFontSize={powerSlideType.slideTitle.fontSize}
                  ariaLabel={`powerslide-field-values-${index + 1}`}
                  typeStyle="statNumber"
                  color={surface.accent}
                  surface={surface}
                  paddingInline={powerSlideBlockPadding.stat.paddingInline}
                  paddingBlock={powerSlideBlockPadding.stat.paddingBlock}
                  align="center"
                  readOnly={readOnly}
                  variables={variables}
                  languageShortcode={languageShortcode}
                  getValue={getValue}
                  changeValue={changeValue}
                />
                <PowerSlideTextBlock
                  localizationKey={labelKeys[index] ?? ""}
                  placeholder={`${labels.label} ${index + 1}`}
                  ariaLabel={`powerslide-field-labels-${index + 1}`}
                  typeStyle="caption"
                  color={surface.inkCaption}
                  surface={surface}
                  paddingInline={powerSlideBlockPadding.stat.paddingInline}
                  paddingBlock={powerSlideBlockPadding.stat.paddingBlock}
                  align="center"
                  readOnly={readOnly}
                  variables={variables}
                  languageShortcode={languageShortcode}
                  getValue={getValue}
                  changeValue={changeValue}
                />
              </div>
            ))}
          </div>
          {!readOnly && canAddPowerSlideItem(slide, values.id) && (
            <button
              type="button"
              aria-label="powerslide-add-values"
              onClick={() =>
                onUpdateSlide(
                  // One request for both keys: the value and its label are added
                  // together, so they are allocated together.
                  addPowerSlideItem(slide, values.id, (amount) =>
                    Array.from({ length: amount }, getNewKey),
                  ),
                )
              }
              style={{
                display: "flex",
                alignSelf: "flex-start",
                alignItems: "center",
                gap: 8,
                paddingInline: powerSlideBlockPadding.listItem.paddingInline,
                paddingBlock: powerSlideBlockPadding.listItem.paddingBlock,
                border: `1px solid ${surface.fieldBorder}`,
                borderRadius: powerSlideRadius.sm,
                color: surface.accent,
                ...powerSlideType.caption,
              }}
            >
              <AddIcon style={{ width: 22, height: 22 }} />
              Add {values.label.toLowerCase()}
            </button>
          )}
        </div>
      </>
    );
  };

  const assetSlot = (
    slot: (typeof template.assetSlots)[number],
    extra?: {
      fullBleed?: boolean;
      children?: React.ReactNode;
      style?: CSSProperties;
    },
  ) => (
    <PowerSlideAssetSlot
      key={slot.id}
      slot={slot}
      assetId={getPowerSlideAssetId(slide, slot.id)}
      surface={surface}
      onChange={(assetId) =>
        onUpdateSlide(setPowerSlideAsset(slide, slot.id, assetId))
      }
      fullBleed={extra?.fullBleed}
      style={extra?.style}
      readOnly={readOnly}
    >
      {extra?.children}
    </PowerSlideAssetSlot>
  );

  /** Image and Video: title, one asset filling the middle, optional caption. */
  const mediaLayout = () => {
    const slot = template.assetSlots[0];
    if (!slot) return null;
    const filled = Boolean(getPowerSlideAssetId(slide, slot.id));

    return (
      <>
        {textBlock(POWER_SLIDE_TITLE_FIELD, "slideTitle", surface.ink)}
        {assetSlot(slot, {
          style: { flex: "1 0 0", minHeight: 0, width: "100%" },
          children:
            slot.kind === "video" && filled ? <PowerSlideVideoChrome /> : null,
        })}
        {textBlock("caption", "body", surface.inkCaption)}
      </>
    );
  };

  /** Side by Side, Three Visuals and Gallery: one grid, different slot counts. */
  const gridLayout = () => {
    const columns = template.gridColumns ?? template.assetSlots.length;
    const captions = field(template, "captions");
    const captionKeys = captions
      ? getPowerSlideFieldKeys(slide, captions.id)
      : [];
    const rows: (typeof template.assetSlots)[] = [];
    for (let index = 0; index < template.assetSlots.length; index += columns) {
      rows.push(template.assetSlots.slice(index, index + columns));
    }

    return (
      <>
        {textBlock(POWER_SLIDE_TITLE_FIELD, "slideTitle", surface.ink)}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flex: "1 0 0",
            minHeight: 0,
            gap: powerSlideMedia.gridGap,
            width: "100%",
          }}
        >
          {rows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              style={{
                display: "flex",
                flex: "1 0 0",
                minHeight: 0,
                alignItems: "stretch",
                gap: powerSlideMedia.gridGap,
                width: "100%",
              }}
            >
              {row.map((slot, columnIndex) => {
                const slotIndex = rowIndex * columns + columnIndex;
                return (
                  <div
                    key={slot.id}
                    style={{
                      display: "flex",
                      flex: "1 0 0",
                      minWidth: 0,
                      flexDirection: "column",
                      gap: powerSlideMedia.slotCaptionGap,
                    }}
                  >
                    {assetSlot(slot, {
                      style: { flex: "1 0 0", minHeight: 0, width: "100%" },
                    })}
                    {captions &&
                      textBlock(captions.id, "caption", surface.inkCaption, {
                        padding: powerSlideBlockPadding.listItem,
                        index: slotIndex,
                        localizationKey: captionKeys[slotIndex],
                      })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </>
    );
  };

  /**
   * Overlay Image is the only template that is not a padded column: the asset
   * is full bleed and the text sits over a scrim at its own inset.
   */
  const overlayLayout = () => {
    const slot = template.assetSlots[0];
    if (!slot) return null;
    const filled = Boolean(getPowerSlideAssetId(slide, slot.id));

    return (
      <div style={{ position: "absolute", inset: 0 }}>
        {assetSlot(slot, {
          fullBleed: true,
          style: { position: "absolute", inset: 0 },
        })}
        {/* Hidden while empty: the flat dark ground already carries the text. */}
        {filled && (
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: powerSlideOverlay.scrimHeight,
              background: `linear-gradient(to bottom, ${powerSlideOverlay.scrimFrom}, ${powerSlideOverlay.scrimTo})`,
            }}
          />
        )}
        <div
          style={{
            position: "absolute",
            left: powerSlideOverlay.textLeft,
            bottom: powerSlideOverlay.textBottom,
            width: powerSlideOverlay.textWidth,
            display: "flex",
            flexDirection: "column",
            gap: powerSlideOverlay.textGap,
          }}
        >
          {textBlock(POWER_SLIDE_TITLE_FIELD, "slideTitle", surface.ink, {
            padding: {
              paddingInline: powerSlideOverlay.blockPaddingInline,
              paddingBlock: powerSlideOverlay.blockPaddingBlock,
            },
          })}
          {textBlock("caption", "caption", surface.ink, {
            padding: {
              paddingInline: powerSlideOverlay.blockPaddingInline,
              paddingBlock: powerSlideOverlay.blockPaddingBlock,
            },
          })}
        </div>
      </div>
    );
  };

  const body = () => {
    if (template.bodyLayout === "list") return listLayout();
    if (template.bodyLayout === "stats") return statsLayout();
    if (template.bodyLayout === "media") return mediaLayout();
    if (template.bodyLayout === "grid") return gridLayout();
    if (template.bodyLayout === "overlay") return overlayLayout();
    if (template.surface === "title") return titleLayout;
    if (template.surface === "section") return sectionLayout;
    return basicLayout;
  };

  const isOverlay = template.bodyLayout === "overlay";
  const stacksWithGap =
    template.bodyLayout === "stats" ||
    template.bodyLayout === "media" ||
    template.bodyLayout === "grid";

  return (
    <PowerSlideStage
      background={surface.background}
      testId={`powerslide-canvas-${template.id}`}
    >
      <div
        data-testid="powerslide-slide-root"
        style={{
          width: "100%",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: centred ? "center" : "flex-start",
          justifyContent: centred ? "center" : "flex-start",
          gap: stacksWithGap ? powerSlideMetrics.statsTitleGap : undefined,
          // Overlay Image is full bleed, so it gets no slide padding at all.
          paddingInline: isOverlay ? 0 : slidePadding.paddingInline,
          paddingBlock: isOverlay ? 0 : slidePadding.paddingBlock,
          position: isOverlay ? "relative" : undefined,
        }}
      >
        {body()}
      </div>
    </PowerSlideStage>
  );
}
